import BateriaWs101 from "@/components/BateriaWs101";
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { capacidades } from "@/lib/capacidades";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TecnicoBadge } from "@/components/StatusBits";
import { ReadinessBadge } from "@/components/admin/readiness";
import { fmtFecha } from "@/lib/format";
import SiteForm from "@/components/forms/SiteForm";
import DeviceForm from "@/components/forms/DeviceForm";
import { RadarEstadoVS373 } from "@/components/site/RadarEstadoVS373";
import { BateriaMoko } from "@/components/site/BateriaMoko";
import { etiquetaTipoDispositivo } from "@/lib/dispositivoLabel";
import PersonasTab from "@/components/admin/PersonasTab";
import EspaciosTab from "@/components/admin/EspaciosTab";
import PersonaObjectPage from "@/components/admin/PersonaObjectPage";
import PersonaFichaOperativa from "@/components/admin/PersonaFichaOperativa";
import PersonaWizard from "@/components/admin/PersonaWizard";
import ZonaPeligro from "@/components/lifecycle/ZonaPeligro";
import ConfiguracionLifecycle from "@/components/lifecycle/ConfiguracionLifecycle";
import CommissioningPorCama from "@/components/site/CommissioningPorCama";
import { ExportarReporteSitio } from "@/components/reportes/ReporteControles";
import {
  ArrowLeft, Pencil, Plus, MapPin, Cpu, Wifi, WifiOff, HelpCircle, Phone, Mail, ShieldAlert,
  CheckCircle2, AlertTriangle, Settings,
} from "lucide-react";

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{value || "—"}</p>
    </div>
  );
}

// C-7: reintento de SÓLO la lectura de espacios (zonas/camas del sitio vienen por rutas distintas:
// el detalle del sitio trae las zonas y /camas trae las camas). Es una LECTURA: nunca reenvía una
// escritura y siempre deja un resultado visible.
function reintentarLecturaCamas(loadCamas, setCamasError) {
  return loadCamas()
    .then(() => {
      toast.success("Espacios actualizados.", { description: "Las camas del sitio ya están al día." });
      return true;
    })
    .catch(() => {
      setCamasError(true);
      toast.warning("Seguimos sin poder actualizar los espacios.", {
        description: "Se conserva la última lectura de camas; el resto del sitio sí está al día. Puedes reintentar; no hay nada que volver a capturar.",
        duration: 15000,
        action: { label: "Reintentar espacios", onClick: () => { reintentarLecturaCamas(loadCamas, setCamasError); } },
      });
      return false;
    });
}

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSupervisor, user, isExternal } = useAuth();
  const caps = capacidades(user);
  const [data, setData] = useState(null);
  const [camas, setCamas] = useState([]);
  const [camasError, setCamasError] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [contactosRestringidos, setContactosRestringidos] = useState(false);
  const [clientesRestringidos, setClientesRestringidos] = useState(false);
  const [siteForm, setSiteForm] = useState(false);
  const [deviceForm, setDeviceForm] = useState({ open: false, device: null });
  // CONSOLA-02: los diálogos se controlan por estado (sin DialogTrigger), así que el foco se
  // devuelve a mano al control que los abrió cuando se cierran.
  const disparadorRef = useRef(null);
  const recordarDisparador = (ev) => { disparadorRef.current = ev?.currentTarget || null; };
  const devolverFoco = () => {
    const t = disparadorRef.current;
    if (t && t.isConnected) setTimeout(() => t.focus(), 0);
  };
  const [wizardOpen, setWizardOpen] = useState(false);
  const [configAbierta, setConfigAbierta] = useState(false);
  // GC94 · R02 — EXPEDIENTE OPERATIVO por ID. La lista sigue siendo ligera; al abrir una persona
  // se obtiene su detalle autorizado (GET /personas/{id}/ficha) en vez de usar la tarjeta reducida.
  const [ficha, setFicha] = useState(null);
  const [fichaEstado, setFichaEstado] = useState(null);
  // Deep-link desde Pendientes de alta (v2.10.0): ?persona=<id>&seccion=<tab>
  const [searchParams, setSearchParams] = useSearchParams();
  const [selPersonaId, setSelPersonaId] = useState(searchParams.get("persona") || null);
  const personaSeccion = searchParams.get("seccion") || "resumen";
  // NAV-002: al volver al sitio los parámetros de la ficha ya no aplican. Se limpian para que la
  // URL y la pantalla representen el mismo estado (un refresh se queda en el sitio).
  const volverAlSitio = () => {
    setSelPersonaId(null);
    if (searchParams.has("persona") || searchParams.has("seccion")) {
      const n = new URLSearchParams(searchParams);
      n.delete("persona");
      n.delete("seccion");
      setSearchParams(n, { replace: true });
    }
  };

  // CONSOLA-02: `load` DEVUELVE la promesa del refresco para que quien guarda pueda esperar la
  // vista actualizada (listado/contador/ficha) antes de anunciar el éxito.
  // r4 (C-7): antes el fallo de /camas se tragaba con .catch(() => {}) y el refresco se anunciaba
  // como completo. Ahora, si el detalle del sitio responde y la lectura de camas NO, se conservan
  // los datos disponibles y `load` resuelve identificando el fallo PARCIAL (guardado.js lo usa
  // para no prometer una vista completa) con reintento de esa sola lectura.
  const loadCamas = useCallback(
    () => api.get(`/camas?sitio_id=${id}`).then((r) => { setCamas(r.data); setCamasError(false); return true; }),
    [id]);
  const reintentarCamas = useCallback(
    () => reintentarLecturaCamas(loadCamas, setCamasError), [loadCamas]);
  const load = useCallback(() => {
    const camasPromesa = loadCamas().then(() => null).catch(() => {
      setCamasError(true);
      return {
        parcial: true,
        aviso: "Las camas del sitio no se pudieron actualizar: se conserva la última lectura. Lo demás del sitio sí está al día.",
        accionLabel: "Reintentar espacios",
        accion: () => { reintentarLecturaCamas(loadCamas, setCamasError); },
      };
    });
    return api.get(`/sitios/${id}`)
      .then((r) => setData(r.data))
      .then(() => camasPromesa);
  }, [id, loadCamas]);
  const loadContactos = useCallback(() => api.get("/contactos").then((r) => setContactos(r.data)), []);
  // A (post-UAT): un 403 ESPERADO en un catálogo interno (contactos/clientes) que este rol no puede
  // consultar NO debe romper la ficha del sitio. Sólo marca ese catálogo como restringido; 401 lo
  // sigue manejando el interceptor de sesión y cualquier otro error permanece VISIBLE.
  const lecturaAuxiliar = useCallback((promesa, marcarRestringido, queFallo) => promesa.catch((e) => {
    if (e?.response?.status === 403) { marcarRestringido(true); return; }
    toast.error(`No se pudo leer ${queFallo}.`, { description: formatApiErrorDetail(e?.response?.data?.detail) });
  }), []);

  useEffect(() => {
    load().catch(() => toast.error("No se pudo cargar el sitio."));
  }, [load]);

  // La ficha se limpia SIEMPRE al cambiar de residente: navegar rápido entre expedientes no mezcla
  // datos de dos personas.
  useEffect(() => {
    if (!selPersonaId || !isExternal) { setFicha(null); setFichaEstado(null); return undefined; }
    let vivo = true;
    setFicha(null);
    setFichaEstado("cargando");
    api.get(`/personas/${selPersonaId}/ficha`)
      .then((r) => { if (vivo) { setFicha(r.data); setFichaEstado("listo"); } })
      .catch((e) => {
        if (!vivo) return;
        setFicha(null);
        setFichaEstado(e?.response?.status === 404 ? "no_disponible" : "error");
      });
    return () => { vivo = false; };
  }, [selPersonaId, isExternal]);

  // v2.13.4 · PRIVACIDAD: el EXPEDIENTE de una persona ya no consume catálogos globales (lee
  // /api/personas/{id}/contactos). La libreta global y el catálogo de contratantes sólo se traen
  // cuando el supervisor abre el asistente de ALTA, que sí necesita el catálogo para dar de alta.
  useEffect(() => {
    if (!wizardOpen) return;
    lecturaAuxiliar(loadContactos(), setContactosRestringidos, "la libreta de contactos");
    lecturaAuxiliar(api.get("/clientes").then((r) => setClientes(r.data)), setClientesRestringidos, "el catálogo de contratantes");
  }, [wizardOpen, loadContactos, lecturaAuxiliar]);

  if (!data) return <p className="text-slate-400">Cargando…</p>;

  const { sitio, cliente, personas, zonas } = data;
  // A (post-UAT): el read-model de un rol institucional NO trae el inventario de dispositivos ni el
  // cliente del sitio. Ausencia de dato != cero dispositivos: se distingue para no presentar un
  // vacío como si estuviera confirmado.
  const dispositivosDisponibles = Array.isArray(data.dispositivos);
  const dispositivos = dispositivosDisponibles ? data.dispositivos : [];
  const clienteDisponible = Object.prototype.hasOwnProperty.call(data, "cliente");
  // A (FIX 02): el resumen de readiness del encabezado/Resumen sólo viaja en el DTO interno. Cuando
  // no viene, se DERIVA del mismo selector autorizado que usa la pestaña Personas
  // (personas[].readiness.estado) en vez de caer a ceros inventados. Una persona con readiness
  // desconocido NO se clasifica como lista ni incompleta, y si no hay ninguna señal el contador se
  // muestra como «—»: dato ausente no es cero.
  const activas = (personas || []).filter((p) => p.estado !== "Inactiva");
  const conReadiness = activas.filter((p) => p.readiness?.estado);
  const res = sitio.readiness_resumen || (conReadiness.length ? {
    total: activas.length,
    listos: conReadiness.filter((p) => p.readiness.estado === "listo").length,
    incompletos: conReadiness.filter((p) => p.readiness.estado !== "listo" && p.readiness.estado !== "suspendido").length,
  } : null);
  const totalPersonas = typeof sitio.num_personas === "number" ? sitio.num_personas : (res ? res.total : null);
  const numDispositivos = typeof sitio.num_dispositivos === "number" ? sitio.num_dispositivos
    : (dispositivosDisponibles ? dispositivos.length : null);
  const dato = (v) => (v === null || v === undefined ? "—" : v);
  const selPersona = personas.find((p) => p.id === selPersonaId);

  const toggleEstadoSitio = async () => {
    const accion = sitio.estado === "Inactivo" ? "activar" : "desactivar";
    try {
      await api.post(`/sitios/${sitio.id}/${accion}`);
      toast.success(accion === "activar" ? "Sitio activado." : "Sitio desactivado.");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const retirarDevice = async (d) => {
    try { await api.post(`/dispositivos/${d.id}/retirar`); toast.success("Dispositivo retirado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- Vista de detalle de persona (object page) ----
  if (selPersona && isExternal) {
    // Enfermería institucional: expediente operativo de SÓLO LECTURA desde la ficha autorizada.
    return <PersonaFichaOperativa ficha={ficha} estado={fichaEstado} onBack={volverAlSitio} />;
  }
  if (selPersona) {
    return (
      <PersonaObjectPage
        persona={selPersona}
        sitio={sitio}
        dispositivos={dispositivos}
        contactos={contactos}
        clientes={clientes}
        refetchContactos={loadContactos}
        initialTab={personaSeccion}
        camas={camas}
        zonas={zonas || []}
        onBack={volverAlSitio}
        onSaved={load}
      />
    );
  }

  return (
    // El puente de tema nocturno (index.css · `.tema-noche-auto`) faltaba en esta página: sus
    // controles declaran `bg-white` a mano mientras el texto hereda `--foreground`, que en noche
    // es casi blanco -> «Activos», «Orden: nombre» y el buscador quedaban blanco sobre blanco, y
    // el chip del tipo de sitio (`text-[#012472]`) quedaba navy sobre fondo oscuro. Se reutiliza
    // el sistema de temas existente en vez de recolorear controles uno a uno.
    <div className="tema-noche-auto space-y-6">
      <button onClick={() => navigate("/sitios")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#012472] transition-colors" data-testid="back-to-sites">
        <ArrowLeft strokeWidth={1.5} className="h-4 w-4" /> Volver a sitios
      </button>

      {/* Header object page */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight text-[#012472]">{sitio.nombre || "(sin nombre)"}</h1>
            <span className="text-[10px] uppercase tracking-wide rounded-full bg-[#012472]/5 text-[#012472] px-2 py-0.5">
              {(sitio.modalidad || "residencial") === "institucional" ? "Institucional" : "Residencial"}
            </span>
            {sitio.estado === "Inactivo" && <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">Inactivo</span>}
          </div>
          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin strokeWidth={1.5} className="h-4 w-4" /> {sitio.direccion}, {sitio.ciudad}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="site-readiness-resumen" className="inline-flex items-center gap-2 text-xs">
            <span className="rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 font-medium">{dato(res?.listos)} listos</span>
            <span className={`rounded-full px-2.5 py-1 font-medium ${res?.incompletos > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-500"}`}>{dato(res?.incompletos)} incompletos</span>
          </span>
          <TecnicoBadge estado={sitio.tecnico_estado} ok={sitio.tecnico_ok} detalle={sitio.tecnico_detalle} testId="site-tecnico-badge" />
          {caps.editarSitio && (
            <Button variant="outline" size="sm" className="rounded-lg" onClick={(ev) => { recordarDisparador(ev); setSiteForm(true); }} data-testid="edit-site-button"><Pencil strokeWidth={1.5} className="h-4 w-4 mr-1" /> Editar sitio</Button>
          )}
          {isSupervisor && (
            <Button variant="ghost" onClick={() => setConfigAbierta(true)}
              data-testid="site-configuracion-button" aria-label="Configuración del sitio" title="Configuración"
              className="rounded-xl h-11 w-11 p-0 shrink-0 text-slate-500 hover:text-[#012472] hover:bg-slate-100">
              <Settings className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="personas" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="resumen" data-testid="site-tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="personas" data-testid="site-tab-personas">Personas</TabsTrigger>
          <TabsTrigger value="espacios" data-testid="site-tab-espacios">Espacios</TabsTrigger>
          <TabsTrigger value="dispositivos" data-testid="site-tab-dispositivos">Dispositivos</TabsTrigger>
          <TabsTrigger value="commissioning" data-testid="site-tab-commissioning">Commissioning</TabsTrigger>
        </TabsList>

        {/* Resumen */}
        <TabsContent value="resumen" className="mt-5 space-y-5">
          {/* REPORT-01 — reporte operativo del sitio (PDF e impresión), dentro del sitio
              autorizado y por periodo. No agrega entre sitios. */}
          <ExportarReporteSitio sitioId={sitio.id} sitioNombre={sitio.nombre} />
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <Info label="Referencias para llegar" value={sitio.referencias} />
              <Info label="Notas de acceso" value={sitio.notas_acceso} />
              <div className="border-l border-gray-100 pl-6">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Cliente</p>
                <p className="text-sm font-medium text-[#012472]">{clienteDisponible ? (cliente?.nombre_completo || "Sin cliente asignado") : "No disponible para tu rol"}</p>
                {cliente && (
                  <div className="mt-2 space-y-1 text-sm text-slate-600">
                    {cliente.telefono && <p className="flex items-center gap-1.5"><Phone strokeWidth={1.5} className="h-3.5 w-3.5 text-slate-400" /> {cliente.telefono}</p>}
                    {cliente.correo && <p className="flex items-center gap-1.5"><Mail strokeWidth={1.5} className="h-3.5 w-3.5 text-slate-400" /> {cliente.correo}</p>}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-gray-100 pt-5">
              <div><p data-testid="site-kpi-personas" className="text-2xl font-semibold text-[#012472]">{dato(totalPersonas)}</p><p className="text-xs text-slate-500">Personas</p></div>
              <div><p data-testid="site-kpi-listos" className="text-2xl font-semibold text-emerald-600">{dato(res?.listos)}</p><p className="text-xs text-slate-500" title="Readiness integral: expediente, cobertura técnica y plan de respuesta completos.">Listos para monitoreo</p></div>
              <div><p data-testid="site-kpi-incompletos" className="text-2xl font-semibold text-amber-600">{dato(res?.incompletos)}</p><p className="text-xs text-slate-500">Incompletos</p></div>
              <div><p data-testid="site-kpi-dispositivos" className="text-2xl font-semibold text-[#012472]">{dato(numDispositivos)}</p><p className="text-xs text-slate-500">Dispositivos</p></div>
            </div>
          </div>

          {isSupervisor && sitio.estado === "Inactivo" && (
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="site-banner-inactivo">
              <p className="text-sm text-slate-600">
                Sitio inactivo. No recibe operación ni acceso institucional.
                <span className="block text-xs text-slate-400 mt-0.5">Gestionar en Configuración.</span>
              </p>
            </div>
          )}
        </TabsContent>

        {/* Personas */}
        <TabsContent value="personas" className="mt-5">
          {(contactosRestringidos || clientesRestringidos) && (
            <p data-testid="site-catalogos-restringidos" className="mb-3 text-xs text-slate-500">
              {contactosRestringidos && clientesRestringidos
                ? "Libreta de contactos y catálogo de contratantes: no disponibles para tu rol."
                : contactosRestringidos
                  ? "Libreta de contactos: no disponible para tu rol."
                  : "Catálogo de contratantes: no disponible para tu rol."}
            </p>
          )}
          <PersonasTab personas={personas} onOpen={(p) => setSelPersonaId(p.id)} onAdd={() => setWizardOpen(true)} puedeAgregar={caps.crearPersona} />
        </TabsContent>

        {/* Espacios */}
        <TabsContent value="espacios" className="mt-5">
          <EspaciosTab sitio={sitio} zonas={zonas || []} camas={camas} onReload={load} puedeGestionar={caps.gestionarEspacios}
            camasError={camasError} onReintentarCamas={reintentarCamas} />
        </TabsContent>

        {/* Dispositivos */}
        <TabsContent value="dispositivos" className="mt-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-[#012472]">Dispositivos{dispositivosDisponibles ? ` (${dispositivos.length})` : ""}</h3>
              {caps.gestionarDispositivos && (
                <Button size="sm" className="rounded-lg bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={(ev) => { recordarDisparador(ev); setDeviceForm({ open: true, device: null }); }} data-testid="add-device-button"><Plus strokeWidth={1.75} className="h-4 w-4 mr-1" /> Agregar</Button>
              )}
            </div>
            {!dispositivosDisponibles ? (
              <p data-testid="site-dispositivos-restringido" className="text-sm text-slate-500">
                Inventario de dispositivos: no disponible para tu rol.
              </p>
            ) : dispositivos.length === 0 ? <p className="text-sm text-slate-400">No hay dispositivos registrados.</p> : (
              <div className="divide-y divide-gray-100">
                {dispositivos.map((d) => {
                  const sinPersona = (d.personas_monitoreadas || []).length === 0 && d.registro === "Activo";
                  return (
                    <div key={d.id} data-testid={`device-row-${d.id}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Cpu strokeWidth={1.5} className="h-4 w-4 text-slate-400 shrink-0" />
                          <p className="text-sm font-medium text-[#012472]">
                            {etiquetaTipoDispositivo(d.tipo)} <span className="text-slate-400 font-normal">— {d.ubicacion || "sin ubicación"}</span>
                            {/* UI-016: se muestran los identificadores REALES ya capturados en el
                                alta (modelo, número de serie e identificador de evento). Nunca se
                                fabrica un valor: si falta, se marca como no capturado. */}
                            <span data-testid={`device-ident-${d.id}`} className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-600">
                              {d.numero_serie ? `S/N ${d.numero_serie}`
                                : d.identificador_evento ? `EVT ${d.identificador_evento}`
                                : d.mac_address ? `MAC ${d.mac_address}`
                                : `ID ${String(d.id).slice(0, 8)}`}
                            </span>
                          </p>
                          {d.registro === "Retirado" && <span className="text-xs rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">Retirado</span>}
                        </div>
                        <p className="text-xs text-slate-400 mt-1 ml-6 flex flex-wrap gap-x-2 gap-y-0.5">
                          <span>Modelo: <span data-testid={`device-modelo-${d.id}`} className={d.modelo ? "text-slate-600" : ""}>{d.modelo || "no capturado"}</span></span>
                          <span aria-hidden>·</span>
                          <span>S/N: <span data-testid={`device-serie-${d.id}`} className={d.numero_serie ? "font-mono text-slate-600" : ""}>{d.numero_serie || "no capturado"}</span></span>
                          <span aria-hidden>·</span>
                          <span>ID de evento: <span data-testid={`device-evento-${d.id}`} className={d.identificador_evento ? "font-mono text-slate-600" : ""}>{d.identificador_evento || "no capturado"}</span></span>
                          <span aria-hidden>·</span>
                          <span>MAC: <span data-testid={`device-mac-${d.id}`}>{d.mac_address || "—"}</span></span>
                          <span aria-hidden>·</span>
                          <span>Inst.: {fmtFecha(d.fecha_instalacion)}</span>
                        </p>
                        {sinPersona && <p className="text-xs text-amber-600 mt-1 ml-6 flex items-center gap-1"><ShieldAlert strokeWidth={1.75} className="h-3.5 w-3.5" /> Sin persona asignada.</p>}
                        <RadarEstadoVS373 estado={d.vs373_estado} deviceId={d.id} />
                        <BateriaMoko bateria={d.bateria} deviceId={d.id} />
                        {d.tipo === "MILESIGHT_WS101" && <BateriaWs101 lectura={d.ws101_bateria} intervalo={d.ws101_intervalo_min} />}
                      </div>
                      <div className="flex items-center gap-3 ml-6 sm:ml-0">
                        {/* HEALTH/UI-010: la salud mostrada es la CANÓNICA de C2 (`salud_estado`),
                            no el campo declarativo del alta. Sin evidencia => no confirmable. */}
                        <span data-testid={`device-conect-${d.id}`}
                          data-estado={d.salud_estado === "confirmado_operativo" ? "en_linea" : d.salud_estado === "confirmado_caido" ? "fuera_de_linea" : "sin_datos"}
                          title={d.salud_motivo || undefined}
                          className={`flex items-center gap-1.5 text-sm ${d.registro === "Retirado" ? "opacity-40" : ""}`}>
                          {d.salud_estado === "confirmado_operativo" ? (
                            <><Wifi strokeWidth={1.5} className="h-4 w-4 text-emerald-500" /> <span className="text-slate-600">En línea</span></>
                          ) : d.salud_estado === "confirmado_caido" ? (
                            <><WifiOff strokeWidth={1.5} className="h-4 w-4 text-[#9B2C2C]" /> <span className="text-slate-600">Fuera de línea</span></>
                          ) : (
                            <><HelpCircle strokeWidth={1.5} className="h-4 w-4 text-indigo-500" /> <span className="text-slate-600">No confirmable</span></>
                          )}
                        </span>
                        {caps.gestionarDispositivos && (
                          <Button variant="outline" size="sm" className="rounded-lg" onClick={(ev) => { recordarDisparador(ev); setDeviceForm({ open: true, device: d }); }} data-testid={`edit-device-${d.id}`}>Editar</Button>
                        )}
                        {isSupervisor && d.registro !== "Retirado" && <Button variant="ghost" size="sm" className="rounded-lg text-slate-500" onClick={() => retirarDevice(d)} data-testid={`retire-device-${d.id}`}>Retirar</Button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Commissioning */}
        <TabsContent value="commissioning" className="mt-5 space-y-5">
          {isSupervisor && <CommissioningPorCama sitioId={sitio.id} />}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-1">
            <h3 className="text-base font-medium text-[#012472] mb-1">Plan de respuesta · commissioning por persona</h3>
            {/* GC94: esta vista acredita el PLAN configurado, no el readiness integral para
                monitoreo. Un plan configurado no vuelve «lista» a la persona: los demás ejes
                (expediente y cobertura técnica) se declaran en Personas y en el Resumen. */}
            <p data-testid="commissioning-alcance" className="text-xs text-slate-500 mb-3">
              Sólo el eje <b>plan de respuesta</b>. El estado «Listo para monitoreo» exige además expediente y cobertura técnica.
            </p>
            {personas.filter((p) => p.estado !== "Inactiva").length === 0 ? <p className="text-sm text-slate-400">Sin personas.</p> : (
              <div className="divide-y divide-gray-100">
                {personas.filter((p) => p.estado !== "Inactiva").map((p) => {
                  const ok = p.readiness?.plan_respuesta?.completo;
                  const nombre = p.nombre_completo || p.nombre || "";
                  return (
                    <button key={p.id} data-testid={`commissioning-row-${p.id}`} onClick={() => setSelPersonaId(p.id)} className="w-full text-left py-3 flex items-center justify-between gap-3 hover:bg-slate-50/70 -mx-2 px-2 rounded-lg transition-colors">
                      <span title={nombre} className="text-sm text-slate-700 break-words [overflow-wrap:anywhere] line-clamp-2">{nombre || "(sin nombre)"}</span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${ok ? "text-emerald-600" : "text-amber-700"}`}>
                        {ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                        {ok ? "Plan configurado" : "Plan incompleto"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Forms */}
      <SiteForm open={siteForm} onOpenChange={(v) => { setSiteForm(v); if (!v) devolverFoco(); }} site={sitio} onSaved={load} />
      <DeviceForm open={deviceForm.open} onOpenChange={(v) => { setDeviceForm((s) => ({ ...s, open: v })); if (!v) devolverFoco(); }} device={deviceForm.device} sitioId={sitio.id} personas={personas} zonas={zonas || []} camas={camas} onSaved={load} />
      <PersonaWizard open={wizardOpen} onOpenChange={setWizardOpen} sitioId={sitio.id} sitioNombre={sitio.nombre} modalidad={sitio.modalidad || "residencial"} dispositivos={dispositivos} clientes={clientes} contactos={contactos} refetchContactos={loadContactos} onSaved={load} />

      {isSupervisor && (
        <ConfiguracionLifecycle open={configAbierta} onOpenChange={setConfigAbierta}
          titulo={`Configuración de ${sitio.nombre || "el sitio"}`} inactivo={sitio.estado === "Inactivo"}
          testPrefix="site" labelActivar="Activar sitio" labelDesactivar="Desactivar sitio"
          explicacion="Desactivar retira el acceso institucional a este sitio y conserva todo el historial."
          onToggle={toggleEstadoSitio}
          confirmar={{
            desactivar: {
              titulo: `Desactivar ${sitio.nombre || "el sitio"}`,
              texto: "Este sitio dejará de estar disponible para la operación y para los usuarios institucionales que lo tienen autorizado. El historial, las personas y los casos se conservarán.",
              boton: "Desactivar sitio",
            },
            activar: {
              titulo: `Activar ${sitio.nombre || "el sitio"}`,
              texto: "El sitio volverá a estar disponible para la operación y para los usuarios institucionales autorizados.",
              boton: "Activar sitio",
            },
          }}>
          <ZonaPeligro basePath={`/sitios/${sitio.id}`} tipoLabel="sitio"
            labelToggle="Eliminar sitio permanentemente"
            confirmarCon="el nombre exacto del sitio" confirmarValor={sitio.nombre}
            testPrefix="site" onDeleted={() => navigate("/sitios")} />
        </ConfiguracionLifecycle>
      )}
    </div>
  );
}
